import {ReactNode, useState} from 'react';

// Combined completions-per-bucket chart: two adjacent bar clusters per bucket (Line items |
// Providers), each stacked by person. Follows the same visual language as the TeamActivity
// charts (whose ChartStyles component provides the shared animation/tooltip CSS).

interface TooltipState {
  leftPct: number;
  content: ReactNode;
}

export interface VettingSeriesInput {
  key: string;
  label: string;
  color: string;
  caseValues: number[];
  providerValues: number[];
}

export const MetricLegend = () => (
  <div className="flex items-center gap-3">
    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
      <span style={{width: 9, height: 9, borderRadius: 2.5, background: '#6C6579', display: 'inline-block'}} />
      Line items
    </span>
    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
      <span style={{width: 9, height: 9, borderRadius: 2.5, background: '#6C6579', opacity: 0.5, display: 'inline-block'}} />
      Providers
    </span>
  </div>
);

export const CombinedStackedChart = ({bucketLabels, series}: {bucketLabels: string[]; series: VettingSeriesInput[]}) => {
  const [hover, setHover] = useState<number | null>(null);
  const [tt, setTt] = useState<TooltipState | null>(null);

  const W = 720;
  const H = 240;
  const padL = 32;
  const padR = 8;
  const padT = 10;
  const padB = 26;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const baseY = padT + plotH;

  const n = bucketLabels.length;
  const caseTotals = bucketLabels.map((_, i) => series.reduce((a, s) => a + (s.caseValues[i] || 0), 0));
  const providerTotals = bucketLabels.map((_, i) => series.reduce((a, s) => a + (s.providerValues[i] || 0), 0));
  const maxValue = Math.max(...caseTotals, ...providerTotals, 1);
  const stepY = maxValue > 24 ? Math.ceil(maxValue / 40) * 10 : 5;
  const yMax = Math.max(stepY, Math.ceil(maxValue / stepY) * stepY);
  const yScale = (v: number) => (v / yMax) * plotH;
  const band = plotW / n;
  const barW = Math.min(16, band * 0.24);
  const gap = Math.max(3, band * 0.06);
  const labelEvery = n > 16 ? Math.ceil(n / 15) : 1;

  const gridLines: number[] = [];
  for (let v = 0; v <= yMax; v += stepY) gridLines.push(v);

  const showBucket = (i: number) => {
    setHover(i);
    const caseRows = series.filter((s) => (s.caseValues[i] || 0) > 0);
    const providerRows = series.filter((s) => (s.providerValues[i] || 0) > 0);
    setTt({
      leftPct: ((padL + band * i + band / 2) / W) * 100,
      content: (
        <>
          <div style={{fontWeight: 700, marginBottom: 6}}>{bucketLabels[i]}</div>

          <div style={{display: 'flex', justifyContent: 'space-between', gap: 12, color: '#6C6579', fontWeight: 700}}>
            <span>Line items</span>
            <span>{caseTotals[i]}</span>
          </div>
          {caseRows.map((s) => (
            <div key={`case-${s.key}`} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '1.5px 0'}}>
              <span style={{width: 9, height: 9, borderRadius: 2.5, background: s.color, flex: 'none'}} />
              <span style={{color: '#6C6579'}}>{s.label}</span>
              <span style={{marginLeft: 'auto', fontWeight: 700}}>{s.caseValues[i]}</span>
            </div>
          ))}
          {caseTotals[i] === 0 && <div style={{color: '#9A93A8', fontSize: 11}}>No line item completions</div>}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              color: '#6C6579',
              fontWeight: 700,
              marginTop: 8,
              paddingTop: 6,
              borderTop: '1px solid #ECE7F1',
            }}
          >
            <span>Providers</span>
            <span>{providerTotals[i]}</span>
          </div>
          {providerRows.map((s) => (
            <div key={`provider-${s.key}`} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '1.5px 0'}}>
              <span style={{width: 9, height: 9, borderRadius: 2.5, background: s.color, opacity: 0.6, flex: 'none'}} />
              <span style={{color: '#6C6579'}}>{s.label}</span>
              <span style={{marginLeft: 'auto', fontWeight: 700}}>{s.providerValues[i]}</span>
            </div>
          ))}
          {providerTotals[i] === 0 && <div style={{color: '#9A93A8', fontSize: 11}}>No provider verifications</div>}
        </>
      ),
    });
  };

  const renderStack = (cx: number, metric: 'case' | 'provider', i: number, opacity: number) => {
    const valueOf = (s: VettingSeriesInput) => (metric === 'case' ? s.caseValues[i] : s.providerValues[i]);
    let acc = 0;
    const segs = series.filter((s) => (valueOf(s) || 0) > 0);
    return segs.map((s, si) => {
      const val = valueOf(s);
      const hgt = yScale(val);
      const y = baseY - yScale(acc) - hgt;
      acc += val;
      const isTop = si === segs.length - 1;
      return (
        <rect
          key={`${metric}-${s.key}`}
          className="ta-bar"
          style={{animationDelay: `${i * 22}ms`}}
          x={cx - barW / 2}
          y={y}
          width={barW}
          height={Math.max(0.5, hgt - (isTop ? 0 : 2))}
          rx={isTop ? Math.min(3, barW / 2) : 0}
          fill={s.color}
          opacity={opacity}
        />
      );
    });
  };

  return (
    <div style={{position: 'relative'}}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{display: 'block', width: '100%', height: 'auto'}}
        role="img"
        aria-label="Stacked bar chart of line item completions and provider verifications per period, by team member"
      >
        {gridLines.map((v) => {
          const y = baseY - yScale(v);
          return (
            <g key={`grid-${v}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#ECE7F1" strokeWidth="1" opacity={v === 0 ? 1 : 0.6} />
              <text x={padL - 7} y={y + 3.5} textAnchor="end" fontSize="11" fill="#9A93A8">
                {v}
              </text>
            </g>
          );
        })}
        {bucketLabels.map((label, i) => {
          const cx = padL + band * i + band / 2;
          const caseCX = cx - gap / 2 - barW / 2;
          const providerCX = cx + gap / 2 + barW / 2;
          const active = hover === i;
          const dimmed = hover !== null && hover !== i;
          return (
            <g key={`bucket-${label}-${i}`}>
              <rect x={cx - band / 2 + 1} y={padT} width={band - 2} height={plotH} rx={4} fill="#AE3EC9" opacity={active ? 0.07 : 0} />
              <g opacity={dimmed ? 0.4 : 1}>
                {renderStack(caseCX, 'case', i, 1)}
                {renderStack(providerCX, 'provider', i, 0.5)}
              </g>
              {i % labelEvery === 0 && (
                <text
                  x={cx}
                  y={baseY + 16}
                  textAnchor="middle"
                  fontSize="11"
                  fill={active ? '#201A2C' : '#9A93A8'}
                  fontWeight={active ? 700 : 400}
                >
                  {label}
                </text>
              )}
              <rect
                x={cx - band / 2}
                y={padT}
                width={band}
                height={plotH + padB}
                fill="transparent"
                onMouseEnter={() => showBucket(i)}
                onMouseLeave={() => {
                  setHover(null);
                  setTt(null);
                }}
              />
            </g>
          );
        })}
      </svg>
      <div
        className={`ta-tt${tt ? ' on' : ''}`}
        style={{
          left: `${Math.min(86, Math.max(13, tt?.leftPct ?? 50))}%`,
          top: 16,
        }}
        role="status"
      >
        {tt?.content}
      </div>
    </div>
  );
};
